import React from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Descriptions,
  Tabs,
  Statistic,
  Space,
  Spin,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { campaignsService } from '../../api/campaigns';
import { CampaignStatus } from '../../types';
import { useAuth } from '../../hooks/useAuth';

const { Title, Text, Paragraph } = Typography;

const CampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();

  const { data: campaign, isLoading, error } = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => campaignsService.getCampaignById(id!),
    enabled: Boolean(id),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: CampaignStatus }) =>
      campaignsService.updateCampaignStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    },
  });

  const getStatusColor = (status: CampaignStatus) => {
    const colors = {
      ACTIVE: 'green',
      INACTIVE: 'red',
      DRAFT: 'orange',
      ARCHIVED: 'gray',
    };
    return colors[status] || 'default';
  };

  const getStatusDisplayName = (status: CampaignStatus) => {
    const names = {
      ACTIVE: 'Активная',
      INACTIVE: 'Неактивная',
      DRAFT: 'Черновик',
      ARCHIVED: 'Архивная',
    };
    return names[status] || status;
  };

  const handleStatusToggle = () => {
    if (campaign && id) {
      const newStatus = campaign.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      updateStatusMutation.mutate({ id, status: newStatus });
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <Alert
        message="Ошибка"
        description="Не удалось загрузить данные кампании"
        type="error"
        showIcon
      />
    );
  }

  const tabItems = [
    {
      key: 'overview',
      label: 'Обзор',
      children: (
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}>
            <Card title="Информация о кампании">
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Название">
                  {campaign.name}
                </Descriptions.Item>
                <Descriptions.Item label="Поддомен">
                  <Space>
                    <code>{campaign.subdomain}.oxapp.io</code>
                    <Button
                      type="link"
                      icon={<LinkOutlined />}
                      href={`https://${campaign.subdomain}.oxapp.io`}
                      target="_blank"
                      size="small"
                    >
                      Открыть
                    </Button>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Статус">
                  <Tag color={getStatusColor(campaign.status)}>
                    {getStatusDisplayName(campaign.status)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Владелец">
                  {campaign.owner
                    ? `${campaign.owner.firstName} ${campaign.owner.lastName}`
                    : 'Не назначен'}
                </Descriptions.Item>
                <Descriptions.Item label="Создана">
                  {new Date(campaign.createdAt).toLocaleString('ru-RU')}
                </Descriptions.Item>
                <Descriptions.Item label="Обновлена">
                  {new Date(campaign.updatedAt).toLocaleString('ru-RU')}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          
          <Col xs={24} lg={8}>
            <Row gutter={[0, 24]}>
              <Col span={24}>
                <Card>
                  <Statistic
                    title="Статус кампании"
                    value={getStatusDisplayName(campaign.status)}
                    valueStyle={{ 
                      color: campaign.status === 'ACTIVE' ? '#52c41a' : '#ff4d4f' 
                    }}
                  />
                  <Button
                    type="primary"
                    icon={campaign.status === 'ACTIVE' ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                    onClick={handleStatusToggle}
                    loading={updateStatusMutation.isPending}
                    style={{ marginTop: 16 }}
                  >
                    {campaign.status === 'ACTIVE' ? 'Приостановить' : 'Активировать'}
                  </Button>
                </Card>
              </Col>
              
              <Col span={24}>
                <Card>
                  <Statistic
                    title="Просмотры за месяц"
                    value={12543}
                    suffix="views"
                  />
                </Card>
              </Col>
              
              <Col span={24}>
                <Card>
                  <Statistic
                    title="Конверсия"
                    value={3.2}
                    precision={1}
                    suffix="%"
                  />
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      ),
    },
    {
      key: 'settings',
      label: 'Настройки',
      children: (
        <Card>
          <Title level={4}>Настройки кампании</Title>
          <Paragraph type="secondary">
            Здесь будут настройки кампании: домены, интеграции, уведомления и прочее.
          </Paragraph>
        </Card>
      ),
    },
    {
      key: 'activity',
      label: 'Активность',
      children: (
        <Card>
          <Title level={4}>История активности</Title>
          <Paragraph type="secondary">
            Лог действий и изменений по кампании.
          </Paragraph>
        </Card>
      ),
    },
    {
      key: 'domains',
      label: 'Домены',
      children: (
        <Card>
          <Title level={4}>Управление доменами</Title>
          <Paragraph type="secondary">
            Настройка кастомных доменов для кампании.
          </Paragraph>
        </Card>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/campaigns')}
          >
            Назад к списку кампаний
          </Button>
        </Col>
        <Col>
          {hasRole(['SUPERADMIN', 'ADMIN']) && (
            <Space>
              <Button
                icon={<EditOutlined />}
                onClick={() => navigate(`/campaigns/${id}/edit`)}
              >
                Редактировать
              </Button>
              <Button
                type={campaign.status === 'ACTIVE' ? 'default' : 'primary'}
                icon={campaign.status === 'ACTIVE' ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                onClick={handleStatusToggle}
                loading={updateStatusMutation.isPending}
              >
                {campaign.status === 'ACTIVE' ? 'Приостановить' : 'Активировать'}
              </Button>
            </Space>
          )}
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={2} style={{ margin: 0 }}>
            {campaign.name}
          </Title>
          <Text type="secondary">
            Кампания создана {new Date(campaign.createdAt).toLocaleDateString('ru-RU')}
          </Text>
        </div>

        <Tabs
          defaultActiveKey="overview"
          items={tabItems}
          size="large"
        />
      </Card>
    </div>
  );
};

export default CampaignDetail;
